import { v4 as uuidv4 } from "uuid";
import {
  type PutObjectCommandInput,
  S3Client,
  PutObjectCommand,
} from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import getExtension from "../getExtension";
import { Upload } from "@aws-sdk/lib-storage";
import { ALLOWED_AUDIO_MIME_TYPES } from "@/common/config";

const REGION = import.meta.env.VITE_AWS_REGION;
const BUCKET_NAME = import.meta.env.VITE_S3_BUCKET_NAME;
const IDENTITY_POOL_ID = import.meta.env.VITE_AWS_COGNITO_IDENTITY_POOL_ID;

const s3Client = new S3Client({
  region: REGION,
  forcePathStyle: true,
  endpoint: `https://s3.${REGION}.amazonaws.com`,
  credentials: fromCognitoIdentityPool({
    clientConfig: { region: REGION },
    identityPoolId: IDENTITY_POOL_ID,
  }),
  defaultsMode: "legacy",
});

export interface UploadResult {
  ok: boolean;
  error: string | null;
  key?: string;
  isLoading: boolean;
}

export async function uploadAudioToS3(
  file: File | Blob,
  userId: string | number
): Promise<UploadResult> {
  if (!file.type.startsWith("audio/")) {
    return {
      ok: false,
      error: "El archivo no es de audio.",
      isLoading: false,
    };
  }

  const extension: string = getExtension(file);

  if (file instanceof File) {
    if (!ALLOWED_AUDIO_MIME_TYPES.includes(file.type)) {
      return {
        ok: false,
        error: "Tipo de audio no soportado.",
        isLoading: false,
      };
    }
  }

  const uuid = uuidv4();
  const key = `${userId}-${uuid}.${extension}`;

  try {
    const uint8Array = new Uint8Array(await file.arrayBuffer());
    const uploadParameters: PutObjectCommandInput = {
      Bucket: BUCKET_NAME,
      Key: key,
      Body: uint8Array,
      ContentType: file.type,
      ContentLength: file.size,
    };

    await s3Client.send(new PutObjectCommand(uploadParameters));

    return {
      ok: true,
      error: null,
      key,
      isLoading: false,
    };
  } catch (error: unknown) {
    return {
      ok: false,
      error:
        error instanceof Error
          ? error.message
          : "Error desconocido al subir el archivo.",
      isLoading: false,
    };
  }
}

export const createUploadS3 = async (
  file: Blob,
  id: string
): Promise<Upload> => {
  const uint8Array = new Uint8Array(await file.arrayBuffer());

  return new Upload({
    client: s3Client,
    params: {
      Bucket: BUCKET_NAME,
      Key: id,
      Body: uint8Array,
      ContentType: file.type,
      ContentLength: file.size,
    },
    leavePartsOnError: false,
  });
};
